// Echo Engine documentation content.
//
// Plain data (no JSX) so it can be validated by `tsc` and reused. Every piece of
// user-facing prose is a `LocalizedText`, i.e. an object with BOTH an `en` and a
// `zh` string. That makes English / Mandarin parity a compile-time guarantee:
// omitting a translation is a type error, not a silent gap.
//
// All technical claims here are derived strictly from the checked-in source:
//   - Stage singleton + services: src/app/lib/services/stage/stage.ts
//   - StickerService API: src/app/lib/services/sticker/sticker_service.ts
//   - Public sticker types: src/app/lib/services/sticker/sticker_types.ts
//   - Demo lifecycle usage: src/app/demo/democlient.tsx
// Do not add APIs here that do not exist in the repository.

export const LOCALES = ['en', 'zh'] as const;
export type Locale = (typeof LOCALES)[number];

/** A string that must be provided in every supported language. */
export type LocalizedText = Record<Locale, string>;

export const SECTION_IDS = [
  'overview',
  'lifecycle',
  'services',
  'sticker',
  'workflow',
] as const;
export type SectionId = (typeof SECTION_IDS)[number];

export type DocBlock =
  | { kind: 'p'; text: LocalizedText }
  | { kind: 'subheading'; text: LocalizedText }
  | { kind: 'list'; items: LocalizedText[] }
  | { kind: 'code'; code: string; caption?: LocalizedText }
  | { kind: 'table'; head: LocalizedText[]; rows: LocalizedText[][] };

export interface DocSection {
  id: SectionId;
  title: LocalizedText;
  blocks: DocBlock[];
}

/** UI chrome strings (page title, controls, navigation). */
export const UI = {
  pageTitle: { en: 'Documentation', zh: '文档' },
  pageSubtitle: {
    en: 'Echo Engine — a distributable WebGPU gameplay starter with a single stable sticker API.',
    zh: 'Echo Engine——可分发的 WebGPU 游戏开发起点，提供单一稳定的贴纸 API。',
  },
  languageLabel: { en: 'Language', zh: '语言' },
  onThisPage: { en: 'On this page', zh: '本页目录' },
  backToDemo: { en: 'Back to the demo', zh: '返回演示' },
  openDev: { en: 'Open dev tools', zh: '打开开发者工具' },
} satisfies Record<string, LocalizedText>;

export const languageNames: Record<Locale, string> = {
  en: 'English',
  zh: '中文',
};

/** BCP-47 tag applied to <html lang> when a locale is active. */
export const htmlLang: Record<Locale, string> = {
  en: 'en',
  zh: 'zh-CN',
};

export const SECTIONS: DocSection[] = [
  {
    id: 'overview',
    title: { en: 'Architecture overview', zh: '架构概览' },
    blocks: [
      {
        kind: 'p',
        text: {
          en: 'Echo Engine gives third-party developers a generic WebGPU engine — scene, render, shader, compute, input, resource, and collision services — plus one stable public API for linking gameplay to physical stickers. No proprietary scanning, camera, or backend code is exposed.',
          zh: 'Echo Engine 为第三方开发者提供了一个通用的 WebGPU 引擎——包含场景（scene）、渲染（render）、着色器（shader）、计算（compute）、输入（input）、资源（resource）与碰撞（collision）服务——以及一个稳定的公开 API，用于将游戏玩法与实体贴纸关联。它不暴露任何专有的扫描、摄像头或后端代码。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'A single Stage singleton owns every service. Gameplay code reaches shared services through Stage.instance() and never constructs its own. This keeps engine wiring in one place and keeps the public surface small.',
          zh: '一个 Stage 单例拥有所有服务。游戏代码通过 Stage.instance() 获取共享服务，而绝不自行构造。这样可将引擎接线集中于一处，并让公开接口保持精简。',
        },
      },
      {
        kind: 'code',
        code: `import Stage from '../lib/services/stage/stage';

const stage = Stage.instance();      // lazily constructs the singleton
stage.scene_service;                 // engine services live on the stage
stage.render_service;
stage.sticker_service;               // the one public gameplay API`,
        caption: {
          en: 'Stage.instance() lazily builds the singleton and exposes every service as a field.',
          zh: 'Stage.instance() 惰性创建单例，并将每个服务作为字段暴露出来。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'The scanner is a swappable port', zh: '扫描器是可替换的端口' },
      },
      {
        kind: 'p',
        text: {
          en: 'StickerService depends only on the StickerScannerAdapter interface. When the owner builds a first-party cloud version, they implement the same interface and inject it in stage.ts — gameplay code does not change.',
          zh: 'StickerService 只依赖 StickerScannerAdapter 接口。当所有者构建第一方云端版本时，只需实现同一接口并在 stage.ts 中注入——游戏代码无需改动。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'By default Stage wires the camera scene scanner adapter: tapping scan activates an in-scene camera scanner (a Video SceneObject) that decodes a physical sticker and resolves the session. It uses no IndexedDB and no backend — session/save state is held in memory for the browser session. A leaner in-memory stub adapter is also included and is what the contract tests use.',
          zh: '默认情况下，Stage 接入摄像头场景扫描适配器：点击扫描会在场景中激活一个摄像头扫描器（Video 场景对象），识别实体贴纸并解析会话。它不使用 IndexedDB，也没有后端——会话/存档状态在浏览器会话期间保存于内存中。此外还包含一个更轻量的内存桩适配器（stub），契约测试即使用它。',
        },
      },
    ],
  },
  {
    id: 'lifecycle',
    title: { en: 'Service lifecycle', zh: '服务生命周期' },
    blocks: [
      {
        kind: 'p',
        text: {
          en: 'A view brings the engine up in a fixed order, then tears it down on unmount. The demo page (democlient.tsx) is the reference: it boots the stage, resets the renderer onto a canvas, activates a scene, drives resize, and deactivates cleanly.',
          zh: '视图会按固定顺序启动引擎，并在卸载时销毁它。演示页面（democlient.tsx）是参考实现：它启动 stage、将渲染器重置到画布上、激活场景、处理尺寸变化，并在卸载时干净地停用。',
        },
      },
      {
        kind: 'list',
        items: [
          {
            en: 'Construct — Stage.instance() lazily creates the singleton and all services (shader, resource, collision, render, compute, input, scene, sticker).',
            zh: '构造——Stage.instance() 惰性创建单例及全部服务（shader、resource、collision、render、compute、input、scene、sticker）。',
          },
          {
            en: 'Initialize — await stage.init_services() detects WebGPU support and initializes the shader and compute services. It returns false on unsupported browsers.',
            zh: '初始化——await stage.init_services() 检测 WebGPU 支持并初始化 shader 与 compute 服务；在不支持的浏览器上返回 false。',
          },
          {
            en: 'Attach — stage.render_service.reset(canvas) binds the WebGPU context to the canvas; stage.scene_service.activate("echo-demo", canvas) loads a scene.',
            zh: '绑定——stage.render_service.reset(canvas) 将 WebGPU 上下文绑定到画布；stage.scene_service.activate("echo-demo", canvas) 加载场景。',
          },
          {
            en: 'Resize — stage.render_service.resize(width, height, devicePixelRatio) keeps the drawing buffer matched to layout (driven by a ResizeObserver in the demo).',
            zh: '尺寸调整——stage.render_service.resize(width, height, devicePixelRatio) 使绘制缓冲区与布局保持一致（演示中由 ResizeObserver 驱动）。',
          },
          {
            en: 'Run — stage.start() begins the requestAnimationFrame loop. Each frame ticks the scene, cycles input frames, prepares GPU state, and renders.',
            zh: '运行——stage.start() 启动 requestAnimationFrame 循环。每一帧都会推进场景、切换输入帧、准备 GPU 状态并渲染。',
          },
          {
            en: 'Stop — stage.stop() cancels the animation frame and detaches input; stage.scene_service.deactivate() releases the current scene.',
            zh: '停止——stage.stop() 取消动画帧并解除输入绑定；stage.scene_service.deactivate() 释放当前场景。',
          },
        ],
      },
      {
        kind: 'code',
        code: `useEffect(() => {
  const canvas = canvasRef.current;
  if (!canvas) return;
  let disposed = false;

  async function boot(target: HTMLCanvasElement) {
    const stage = Stage.instance();
    await stage.init_services();               // detect WebGPU + init shader/compute
    if (disposed) return;
    stage.render_service.reset(target);        // bind WebGPU context to the canvas
    await stage.scene_service.activate('echo-demo', target);
  }

  boot(canvas);
  return () => {
    disposed = true;
    Stage.instance().scene_service.deactivate(); // release the scene on unmount
  };
}, []);`,
        caption: {
          en: 'Boot / teardown, adapted from src/app/demo/democlient.tsx.',
          zh: '启动 / 销毁流程，改编自 src/app/demo/democlient.tsx。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'WebGPU is required. Unsupported browsers are redirected to /unsupported. The camera is requested lazily only when a scan starts, never at construction or during server rendering.',
          zh: '本引擎需要 WebGPU。不支持的浏览器会被重定向到 /unsupported。摄像头仅在开始扫描时惰性请求，绝不在构造时或服务端渲染期间触碰。',
        },
      },
    ],
  },
  {
    id: 'services',
    title: { en: 'Core services & responsibilities', zh: '核心服务与职责' },
    blocks: [
      {
        kind: 'p',
        text: {
          en: 'Every service is constructed once by the Stage and reached as a field on Stage.instance(). The table lists the stage-owned services and what each is responsible for.',
          zh: '每个服务都由 Stage 构造一次，并作为 Stage.instance() 上的字段访问。下表列出由 stage 拥有的服务及其各自职责。',
        },
      },
      {
        kind: 'table',
        head: [
          { en: 'Service (field)', zh: '服务（字段）' },
          { en: 'Responsibility', zh: '职责' },
        ],
        rows: [
          [
            { en: 'shader_service', zh: 'shader_service' },
            {
              en: 'Owns the WebGPU device and shader modules; initialized in init_services().',
              zh: '拥有 WebGPU 设备与着色器模块；在 init_services() 中初始化。',
            },
          ],
          [
            { en: 'render_service', zh: 'render_service' },
            {
              en: 'Binds the canvas WebGPU context (reset), tracks render objects, and draws each frame (render); handles resize.',
              zh: '绑定画布 WebGPU 上下文（reset）、跟踪渲染对象并逐帧绘制（render）；处理尺寸调整。',
            },
          ],
          [
            { en: 'compute_service', zh: 'compute_service' },
            {
              en: 'Manages GPU compute pipelines and pools; initialized in init_services().',
              zh: '管理 GPU 计算管线与资源池；在 init_services() 中初始化。',
            },
          ],
          [
            { en: 'scene_service', zh: 'scene_service' },
            {
              en: 'Activates/deactivates a scene, ticks it, and prepares GPU state. "echo-demo" loads the minimal starter scene.',
              zh: '激活/停用场景、推进场景并准备 GPU 状态。“echo-demo” 会加载最小的起步场景。',
            },
          ],
          [
            { en: 'input_service', zh: 'input_service' },
            {
              en: 'Attaches to the canvas and cycles per-frame input state (begin/end frame).',
              zh: '绑定到画布并逐帧循环输入状态（begin/end frame）。',
            },
          ],
          [
            { en: 'resource_service', zh: 'resource_service' },
            {
              en: 'Loads and owns GPU resources (meshes, textures) via the WebGPU device context.',
              zh: '通过 WebGPU 设备上下文加载并拥有 GPU 资源（网格、纹理）。',
            },
          ],
          [
            { en: 'collision_service', zh: 'collision_service' },
            {
              en: 'Provides collision volumes and raycasting, and draws collision debug geometry.',
              zh: '提供碰撞体与射线检测，并绘制碰撞调试几何体。',
            },
          ],
          [
            { en: 'sticker_service', zh: 'sticker_service' },
            {
              en: 'The single public gameplay API for stickers. Backed by a swappable scanner adapter (the camera scene scanner by default).',
              zh: '面向游戏玩法的唯一公开贴纸 API。由可替换的扫描器适配器支撑（默认为摄像头场景扫描器）。',
            },
          ],
        ],
      },
      {
        kind: 'subheading',
        text: { en: 'Stable vs. internal', zh: '稳定接口与内部实现' },
      },
      {
        kind: 'p',
        text: {
          en: 'Import freely from sticker_service.ts (and the types it re-exports) and from the Stage singleton and its services. Do not import anything under lib/services/sticker/internal/*, the StickerScannerAdapter port, Firebase, or any api/* route from gameplay code. An ESLint rule and npm run check:boundaries enforce this for src/app/demo and src/app/scene.',
          zh: '可自由从 sticker_service.ts（及其再导出的类型）以及 Stage 单例与其服务中导入。游戏代码不得导入 lib/services/sticker/internal/* 下的任何内容、StickerScannerAdapter 端口、Firebase，或任何 api/* 路由。ESLint 规则与 npm run check:boundaries 会对 src/app/demo 和 src/app/scene 强制执行此约束。',
        },
      },
    ],
  },
  {
    id: 'sticker',
    title: { en: 'StickerService usage', zh: 'StickerService 用法' },
    blocks: [
      {
        kind: 'p',
        text: {
          en: 'StickerService is stage-owned. Obtain the shared instance from the stage — do not construct your own. scan() never rejects for expected outcomes; inspect the discriminated ScanResult instead. Codes are opaque decimal strings and save data is free-form JSON; inputs and outputs are deep-cloned so you cannot mutate engine state by accident.',
          zh: 'StickerService 由 stage 拥有。请从 stage 获取共享实例，不要自行构造。对于预期结果，scan() 从不 reject；应改为检查带判别属性的 ScanResult。贴纸码是不透明的十进制字符串，存档数据为自由格式 JSON；输入与输出都会被深拷贝，因此你无法意外修改引擎状态。',
        },
      },
      {
        kind: 'code',
        code: `import Stage from '../lib/services/stage/stage';
import type { JsonObject, StickerSession } from '../lib/services/sticker/sticker_service';

const stickers = Stage.instance().sticker_service; // shared instance

// Bring up scanning and get an anonymous session + save game.
const result = await stickers.scan();   // optional: { signal, preferredCode }
if (result.status === 'scanned') {
  const { code, tag, linkedCodes, save } = result.session; // save is free-form JSON
  console.log(tag.id, tag.name);        // ProjectTag: e.g. "series-alpha", "Series Alpha"
  await stickers.updateSave((prev) => ({ ...prev, score: (prev.score ?? 0) + 1 }));
} else if (result.status === 'cancelled') {
  // user aborted via AbortSignal
} else {
  console.error(result.error.code, result.error.message); // typed StickerError
}`,
        caption: {
          en: 'The core scan → inspect → updateSave flow.',
          zh: '核心流程：扫描 → 检查结果 → 更新存档。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'Public methods', zh: '公开方法' },
      },
      {
        kind: 'table',
        head: [
          { en: 'Method', zh: '方法' },
          { en: 'Purpose', zh: '用途' },
        ],
        rows: [
          [
            { en: 'scan(options?)', zh: 'scan(options?)' },
            {
              en: "Bring up scanning; resolves { status: 'scanned' | 'cancelled' | 'error' }. Accepts { signal, preferredCode }.",
              zh: "启动扫描；解析为 { status: 'scanned' | 'cancelled' | 'error' }。可接受 { signal, preferredCode }。",
            },
          ],
          [
            { en: 'getSession()', zh: 'getSession()' },
            {
              en: 'Current session (with cloned save) or null.',
              zh: '当前会话（存档为克隆副本）或 null。',
            },
          ],
          [
            { en: 'getSave()', zh: 'getSave()' },
            {
              en: 'Cloned free-form save object, or null.',
              zh: '克隆的自由格式存档对象，或 null。',
            },
          ],
          [
            { en: 'updateSave(next | updater)', zh: 'updateSave(next | updater)' },
            {
              en: 'Validate + persist save JSON; returns the stored (cloned) save. Throws not_scanning without a session.',
              zh: '校验并持久化存档 JSON；返回已存储的（克隆）存档。无会话时抛出 not_scanning。',
            },
          ],
          [
            { en: 'getLinkedCodes()', zh: 'getLinkedCodes()' },
            {
              en: "Codes linked to the current save (e.g. '1000000001' ↔ '1000000002').",
              zh: "与当前存档关联的代码（例如 '1000000001' ↔ '1000000002'）。",
            },
          ],
          [
            { en: 'endSession()', zh: 'endSession()' },
            {
              en: 'End the runtime session. Does not delete persistent save data.',
              zh: '结束运行时会话。不会删除持久化的存档数据。',
            },
          ],
          [
            { en: 'subscribe(listener)', zh: 'subscribe(listener)' },
            {
              en: 'Observe session/save changes; called immediately with the current snapshot. Returns an unsubscribe function.',
              zh: '订阅会话/存档变化；订阅时立即以当前快照回调一次。返回取消订阅函数。',
            },
          ],
          [
            { en: 'getDemoStickers()', zh: 'getDemoStickers()' },
            {
              en: 'Fixture codes (empty for production adapters).',
              zh: '固定演示码（生产适配器返回空）。',
            },
          ],
        ],
      },
      {
        kind: 'subheading',
        text: { en: 'Reacting to changes', zh: '响应变化' },
      },
      {
        kind: 'code',
        code: `// subscribe() fires immediately with the current session, then on every
// scan / updateSave / endSession. It returns an unsubscribe function.
const unsubscribe = stickers.subscribe((session) => {
  if (session) {
    console.log('active code', session.code, 'save', session.save);
  } else {
    console.log('no active session');
  }
});

// later, when your view unmounts:
unsubscribe();`,
      },
      {
        kind: 'subheading',
        text: { en: 'Sessions & fixtures', zh: '会话与演示码' },
      },
      {
        kind: 'p',
        text: {
          en: 'A scanned StickerSession carries sessionId, code, a persistent saveGameId, a status (not_activated | activated | used), a project tag, linkedCodes, and the free-form save. A successfully scanned code is always used, and its saveGameId is stable across scans of the same or linked codes.',
          zh: '扫描得到的 StickerSession 携带 sessionId、code、持久化的 saveGameId、status（not_activated | activated | used）、项目标签（tag）、linkedCodes 以及自由格式的 save。成功扫描的代码始终为 used，且其 saveGameId 在扫描相同或关联代码时保持稳定。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'The bundled in-memory stub adapter (used by the contract tests) exposes fixture codes for zero-config testing:',
          zh: '随附的内存桩适配器（供契约测试使用）提供了固定演示码，便于零配置测试：',
        },
      },
      {
        kind: 'list',
        items: [
          {
            en: "1000000001, 1000000002 — linked to the same save (exercises linkedCodes); tagged Series Alpha.",
            zh: '1000000001、1000000002——关联到同一存档（用于演示 linkedCodes）；标签为 Series Alpha。',
          },
          {
            en: '1000000003 — an independent save; tagged Series Beta.',
            zh: '1000000003——独立存档；标签为 Series Beta。',
          },
          {
            en: "9999999999 — a fault fixture; scanning it yields { status: 'error' } with StickerError code scan_failed.",
            zh: "9999999999——故障固定码；扫描它会返回 { status: 'error' }，StickerError 代码为 scan_failed。",
          },
        ],
      },
      {
        kind: 'p',
        text: {
          en: 'With the default camera scene scanner, a bare scan() activates the in-scene camera and resolves when a sticker stabilizes (or reports unsupported where no camera exists). It exposes no fixtures. Passing a preferredCode opens that specific code directly without the camera — this is how the unit tests exercise the full session/save flow with no hardware.',
          zh: '使用默认的摄像头场景扫描器时，裸调用 scan() 会激活场景内摄像头，并在贴纸稳定后解析（若无摄像头则报告 unsupported）。它不提供固定演示码。传入 preferredCode 会直接打开该特定代码而不启用摄像头——单元测试正是借此在无硬件的情况下演练完整的会话/存档流程。',
        },
      },
      {
        kind: 'code',
        code: `// Open a specific code directly (no camera): used by tests.
const result = await stickers.scan({ preferredCode: '1000000003' });

// Cancellable scan: abort maps to { status: 'cancelled' }.
const controller = new AbortController();
const pending = stickers.scan({ signal: controller.signal });
controller.abort();
const outcome = await pending; // { status: 'cancelled' }`,
        caption: {
          en: 'preferredCode opens a code without the camera; an AbortSignal cancels a scan.',
          zh: 'preferredCode 无需摄像头即可打开某个代码；AbortSignal 取消扫描。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'Project tags', zh: '项目标签' },
      },
      {
        kind: 'p',
        text: {
          en: 'Every sticker code belongs to a batch, and every batch belongs to a project that owns a catalog of tags. Recognition resolves that reference and returns the complete tag on every scanned session as result.session.tag. The shape is the shared ProjectTag: { id: string, name: string }. The id is stable identity — persist and compare on it — while name is display-only and may change. Tag names are trimmed and unique per project (case-insensitively).',
          zh: '每个贴纸码都归属于一个批次（batch），而每个批次都归属于一个拥有标签目录的项目（project）。识别时会解析该引用，并在每个扫描会话上通过 result.session.tag 返回完整标签。其形状为共享的 ProjectTag：{ id: string, name: string }。id 是稳定标识——请以它进行持久化与比较——而 name 仅用于显示，可能变化。标签名称会被去除首尾空格，并在每个项目内（不区分大小写）唯一。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'The tag is always present. Codes with no explicit tag — including legacy data created before tags existed — normalize non-destructively to the default Unassigned tag ({ id: "unassigned", name: "Unassigned" }, exported as UNASSIGNED_TAG). The StickerService applies this default at the public boundary for any adapter result that omits a tag, so gameplay code can always read session.tag without a null check.',
          zh: '标签始终存在。没有显式标签的代码（包括在标签功能出现之前创建的旧数据）会以非破坏性方式归一化为默认的 Unassigned 标签（{ id: "unassigned", name: "Unassigned" }，导出为 UNASSIGNED_TAG）。对于任何未提供标签的适配器结果，StickerService 会在公开边界应用此默认值，因此游戏代码始终可以读取 session.tag，而无需进行空值检查。',
        },
      },
      {
        kind: 'code',
        code: `import type { ProjectTag } from '../lib/services/sticker/sticker_service';
import { UNASSIGNED_TAG } from '../lib/services/sticker/sticker_service';

const result = await stickers.scan({ preferredCode: '1000000001' });
if (result.status === 'scanned') {
  const tag: ProjectTag = result.session.tag; // { id: 'series-alpha', name: 'Series Alpha' }
  if (tag.id === UNASSIGNED_TAG.id) {
    // untagged / legacy code
  }
}`,
        caption: {
          en: 'Every scanned session exposes a fully-resolved ProjectTag; untagged codes resolve to UNASSIGNED_TAG.',
          zh: '每个扫描会话都暴露完整解析的 ProjectTag；未标记的代码解析为 UNASSIGNED_TAG。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'Production tag resolution (Firestore)', zh: '生产环境标签解析（Firestore）' },
      },
      {
        kind: 'p',
        text: {
          en: 'In a first-party cloud build the tag is resolved server-side by the /api/codes routes against Firestore. The document model is authored by the Echoprint tooling: a project document projects/{projectId} owns a tags array of { id, name }; each batch document projects/{projectId}/batches/{batchId} stores a tagId; and each activated code projects/{projectId}/activatedCodes/{code} records the batchId it was activated under. Recognition follows code.batchId → batch.tagId → project.tags[] and returns the resolved { id, name }.',
          zh: '在第一方云端版本中，标签由 /api/codes 路由在服务端针对 Firestore 解析。文档模型由 Echoprint 工具编写：项目文档 projects/{projectId} 拥有一个由 { id, name } 组成的 tags 数组；每个批次文档 projects/{projectId}/batches/{batchId} 存储一个 tagId；每个已激活代码 projects/{projectId}/activatedCodes/{code} 记录其激活时所属的 batchId。识别按 code.batchId → batch.tagId → project.tags[] 解析，并返回解析出的 { id, name }。',
        },
      },
      {
        kind: 'p',
        text: {
          en: 'Resolution is non-destructive at every hop, mirroring Echoprint\'s shared rules: a code with no batchId, a batch with no/empty tagId, a missing batch or project, and a tagId absent from the catalog all fall back safely — an empty tagId becomes Unassigned, while an unknown id is returned as-is so nothing is silently dropped. The project catalog is read as if the default Unassigned tag is always present (injected on read for legacy projects). All four /api/codes responses (check, use, retrieve/update save, link) carry the resolved tag alongside their existing fields.',
          zh: '解析在每一跳都是非破坏性的，与 Echoprint 的共享规则一致：没有 batchId 的代码、没有或为空 tagId 的批次、缺失的批次或项目，以及目录中不存在的 tagId，都会安全回退——空 tagId 变为 Unassigned，而未知 id 会原样返回，因此不会静默丢失任何内容。读取项目目录时视默认 Unassigned 标签始终存在（对旧项目在读取时注入）。/api/codes 的全部四个响应（check、use、retrieve/update save、link）都会在其既有字段之外携带解析出的标签。',
        },
      },
    ],
  },
  {
    id: 'workflow',
    title: { en: 'Developer workflow', zh: '开发者工作流' },
    blocks: [
      {
        kind: 'p',
        text: {
          en: 'Install dependencies, run the dev server, and verify with the typecheck and build scripts. WebGPU is required to run the demo scene.',
          zh: '安装依赖、运行开发服务器，并用类型检查与构建脚本进行验证。运行演示场景需要 WebGPU。',
        },
      },
      {
        kind: 'code',
        code: `npm install
npm run dev            # http://localhost:3000  (the demo scene)
npm run typecheck      # tsc --noEmit
npm test               # sticker contract + camera adapter / scan-bridge tests
npm run check:boundaries  # gameplay uses only the public surface
npm run build`,
        caption: {
          en: 'Scripts from package.json.',
          zh: '来自 package.json 的脚本。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'Where you write gameplay', zh: '在哪里编写游戏逻辑' },
      },
      {
        kind: 'list',
        items: [
          {
            en: 'src/app/demo/ — the demo page: a fullscreen WebGPU canvas with a small sticker-scan control. It calls Stage.instance().sticker_service.scan(...) and surfaces status through the shared footer via Stage.footer(...).',
            zh: 'src/app/demo/——演示页面：全屏 WebGPU 画布，带一个小的贴纸扫描控件。它调用 Stage.instance().sticker_service.scan(...)，并通过 Stage.footer(...) 在共享页脚中显示状态。',
          },
          {
            en: 'src/app/scene/ — engine scenes. echodemo/echodemoscene.ts is the minimal starter; add your own RenderObjects there.',
            zh: 'src/app/scene/——引擎场景。echodemo/echodemoscene.ts 是最小起步场景；在此添加你自己的 RenderObject。',
          },
        ],
      },
      {
        kind: 'subheading',
        text: { en: 'The /dev tools', zh: '/dev 工具' },
      },
      {
        kind: 'p',
        text: {
          en: 'The /dev page is a Mantine-only developer surface for checking a sticker code and managing its save via the /api/codes endpoints: check a code’s status, create its save (use), retrieve and edit the save JSON, and link a second code to the same save. Every action also shows the code’s resolved project tag (name + id), so you can verify tagged and legacy/Unassigned codes end-to-end. It intentionally does not import the engine or WebGPU and does not touch the Stage or StickerService.',
          zh: '/dev 页面是一个仅使用 Mantine 的开发者界面，通过 /api/codes 端点检查贴纸代码并管理其存档：检查代码状态、创建其存档（use）、检索并编辑存档 JSON，以及将第二个代码链接到同一存档。每个操作还会显示代码解析出的项目标签（名称 + id），因此你可以端到端地验证带标签的代码与旧数据/Unassigned 代码。它有意不引入引擎或 WebGPU，也不触及 Stage 或 StickerService。',
        },
      },
      {
        kind: 'subheading',
        text: { en: 'Swapping in a private adapter (owner only)', zh: '替换为私有适配器（仅所有者）' },
      },
      {
        kind: 'p',
        text: {
          en: 'To ship a first-party build, implement StickerScannerAdapter and inject it where the service is constructed in stage.ts. Gameplay code is unchanged.',
          zh: '要发布第一方版本，实现 StickerScannerAdapter 并在 stage.ts 中构造服务处注入它。游戏代码无需改动。',
        },
      },
      {
        kind: 'code',
        code: `// stage.ts
this.sticker_service = new StickerService({ adapter: new CloudScannerAdapter() });

// createStickerService({ adapter }) is also available for isolated instances (e.g. tests).`,
      },
      {
        kind: 'subheading',
        text: { en: 'Tag administration (owner handoff)', zh: '标签管理（所有者交接）' },
      },
      {
        kind: 'p',
        text: {
          en: 'The /api/codes surface is anonymous and bearer-by-code: a caller proves nothing but possession of a sticker code, so it exposes read/resolve and save operations only — never tag mutation. Authoring and renaming tags remains Echoprint’s job via its existing direct Firestore REST client, which is unchanged. The one invariant a client cannot uphold alone is deleting a tag that is in use: every affected batch must be reassigned to another tag and the project catalog rewritten in a single atomic commit, which exceeds Firestore’s 500-writes-per-commit ceiling once a tag spans enough batches. store.ts ships an admin-only transactional helper, deleteTagWithReassign(project, tagId, reassignToTagId), that performs the reassignment and catalog rewrite atomically using the privileged Admin SDK. It is deliberately not mounted as an HTTP route: the owner wires it behind their authenticated admin channel so it never becomes an unauthenticated privilege-escalation endpoint.',
          zh: '/api/codes 接口是匿名的、以代码作为凭据的：调用方只需持有贴纸代码即可，因此它仅暴露读取/解析与存档操作，绝不暴露标签修改。标签的创建与重命名仍由 Echoprint 通过其既有的直连 Firestore REST 客户端完成，该客户端保持不变。客户端无法独自维护的唯一不变式是删除正在使用中的标签：所有受影响的批次必须在一次原子提交中重新指派到另一个标签并重写项目目录，而一旦某标签覆盖足够多的批次，就会超出 Firestore 每次提交 500 次写入的上限。store.ts 提供了一个仅供管理员使用的事务性辅助函数 deleteTagWithReassign(project, tagId, reassignToTagId)，它使用具有特权的 Admin SDK 原子地完成重新指派与目录重写。它有意不作为 HTTP 路由挂载：所有者应将其接入自己已认证的管理通道，使其绝不会成为未认证的权限提升端点。',
        },
      },
    ],
  },
];
